import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  RiPlaneLine,
  RiCalendar2Line,
  RiTeamLine,
  RiMapPinLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiBuildingLine,
  RiPriceTag3Line,
  RiCheckLine,
  RiTrophyLine,
  RiMedalLine,
  RiTimeLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiFileTextLine,
  RiArrowLeftLine,
  RiUserLine,
  RiDownloadLine
} from '@remixicon/react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { TrustJetPlane, TrustJetBrandLogo } from '@assets/images';
import { buildQuoteAcceptedEmailHtml } from '@/utils/emailTemplates';
import { getAircraftModelName, getOperatorWithLocation } from '@/features/tails/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime, formatPrice, trunc2 } from '@/utils';

// Calculate total cost excluding credit card fee from individual components
const getTotalCost = (option) => trunc2(option?.total_price || option?.final_price);

/**
 * QuotePreviewPage Component
 * Public page for viewing quote details via token-based URL
 * URL: /quotepreview?quote={quoteid}&token={tokenid}
 */
const QuotePreviewPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const quoteId = searchParams.get('quote');
  const token = searchParams.get('token');
  const orgId = searchParams.get('org');
  const optionId = searchParams.get('option');

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Contract signing state
  const [showContract, setShowContract] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [fullName, setFullName] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedAmenities, setExpandedAmenities] = useState({});

  // Drag to scroll refs and state
  const aircraftScrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const selectedAircraftImageRef = useRef(null);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - aircraftScrollRef.current.offsetLeft;
    scrollLeft.current = aircraftScrollRef.current.scrollLeft;
    aircraftScrollRef.current.style.cursor = 'grabbing';
    aircraftScrollRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
    aircraftScrollRef.current.style.cursor = 'grab';
    aircraftScrollRef.current.style.userSelect = '';
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    aircraftScrollRef.current.style.cursor = 'grab';
    aircraftScrollRef.current.style.userSelect = '';
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - aircraftScrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    aircraftScrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const { data: organization } = useQuery({
    queryKey: ["organization", orgId],
    queryFn: () => apiClient.getOrganizationByIdWithToken(orgId, token),
    enabled: !!orgId && !!token,
  });

  // Fetch quote details when page loads (ref guard prevents StrictMode double-fetch)
  const quoteFetchedRef = useRef(false);
  useEffect(() => {
    if (quoteFetchedRef.current) return;
    if (quoteId && token) {
      quoteFetchedRef.current = true;
      loadQuoteDetails();
    } else {
      setError('Invalid quote link. Please check the URL and try again.');
      setLoading(false);
    }
  }, [quoteId, token]);

  // Auto-select bid option and go to contract step when option param is set
  const optionAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (!quote || !optionId || optionAutoSelectedRef.current) return;
    const allOptions = quote.bid_items?.flatMap(bi => bi.bid?.bid_options || []) || [];
    const matched = allOptions.find(opt => opt.bid_option_id === optionId);
    if (matched) {
      optionAutoSelectedRef.current = true;
      handleSelectFlight(matched);
    }
  }, [quote, optionId]);

  const loadQuoteDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch quote using checkout token for public access
       const quoteData = await apiClient.getQuoteByIdWithToken(quoteId, token);
       console.log("quotepreviewdetails", quoteData);
    
      setQuote(quoteData);

      // Mark quote as VIEWED if current status is SENT
      if (quoteData.status === 'SENT') {
        try {
          await apiClient.updateQuote(quoteId, { status: 'VIEWED' }, token);
          setQuote(prev => ({ ...prev, status: 'VIEWED' }));
        } catch (updateErr) {
          console.error('Error updating quote status to VIEWED:', updateErr);
        }
      }
    } catch (err) {
      console.error('Error loading quote details:', err);
      setError(err.message || 'Failed to load quote details. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSelectFlight = (option) => {
    // Show contract signing view
    setSelectedOption(option);
    setShowContract(true);
    setFullName('');
    setAgreeToTerms(false);
  };

  const handleBackToTrips = () => {
    setShowContract(false);
    setSelectedOption(null);
    setFullName('');
    setAgreeToTerms(false);
  };

  const handleProceedToCheckout = async () => {
    if (!agreeToTerms || !fullName.trim() || isSubmitting) return;
    setIsSubmitting(true);

    try {
      // update status to SIGNED only if not already ACCEPTED
      if (quote?.status === 'VIEWED') {
        await apiClient.updateQuote(quoteId, { status: 'SIGNED' }, token);
        setQuote(prev => ({ ...prev, status: 'SIGNED' }));
      
        // Send quote accepted email to broker
        if (organization?.billing_email) {
          try {
            const emailHtml = buildQuoteAcceptedEmailHtml({
              quote,
              selectedBidOption: selectedOption,
              signedBy: fullName,
              organizationName: organization?.name,
              organization
            });

            apiClient.sendMessage({
              From: 'tools@trustjet.ai', 
              To: organization?.billing_email,
              Subject: `TrustJet – Quote Formally Accepted (${quote.quote_number || quoteId})`,
              HtmlBody: emailHtml,
            }, token || null)
              .then(() => console.log('Quote accepted email sent to', quote.client.email))
              .catch((err) => console.error('Failed to send quote accepted email:', err));
          } catch (emailErr) {
            console.error('Failed to build quote accepted email:', emailErr);
          }
        }
      }
    } catch (err) {
      console.error('Error updating quote status to SIGNED:', err);
      setIsSubmitting(false);
      return;
    }

    // Navigate to checkout with quote and token, passing contract data in state
    navigate(`/checkout?quoteid=${quoteId}&token=${token}&option=${selectedOption.bid_option_id}&amount=${getTotalCost(selectedOption)}`, {
      state: {
        contractData: {
          fullName,
          agreeToTerms,
          selectedOption,
          signedAt: new Date().toISOString()
        }
      }
    });
  };

  // Generate and download contract PDF
  const handleDownloadContract = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPos = 20;

    // Helper function to capture image from DOM element using html2canvas
    const captureImageFromElement = async (element) => {
      if (!element) return null;
      try {
        const canvas = await html2canvas(element, {
          useCORS: true,
          allowTaint: true,
          scale: 2
        });
        return canvas.toDataURL('image/jpeg', 0.9);
      } catch {
        return null;
      }
    };

    // Helper function to add text with word wrap
    const addText = (text, fontSize = 10, isBold = false) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = doc.splitTextToSize(text, maxWidth);

      // Check if we need a new page
      if (yPos + (lines.length * fontSize * 0.5) > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        yPos = 20;
      }

      doc.text(lines, margin, yPos);
      yPos += lines.length * fontSize * 0.5 + 4;
    };

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('AvSKY Charter Ventures Inc.', margin, yPos);
    yPos += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Charter Terms and Conditions', margin, yPos);
    yPos += 10;

    // Contact info
    addText('1460 S. McCall Rd. Bldg-A, Unit 3G, Englewood, FL, 34223 | Phone: 720-660-7703 | cameron@iflyavsky.com', 9);
    yPos += 5;

    // Selected flight info if available
    if (selectedOption) {
      doc.setDrawColor(200);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
      addText('SELECTED FLIGHT', 11, true);

      // Try to capture aircraft image from the DOM
      let imageAdded = false;
      if (selectedAircraftImageRef.current) {
        try {
          const imageData = await captureImageFromElement(selectedAircraftImageRef.current);
          if (imageData) {
            const imgWidth = 60;
            const imgHeight = 40;
            doc.addImage(imageData, 'JPEG', margin, yPos, imgWidth, imgHeight);

            // Add text beside image
            const textX = margin + imgWidth + 10;
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text(`Aircraft: ${selectedOption.aircraft?.tail_number || 'N/A'}`, textX, yPos + 10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Model: ${selectedOption.aircraft?.model || selectedOption.aircraft?.make_model || 'N/A'}`, textX, yPos + 18);
            doc.text(`Price: ${formatPrice(getTotalCost(selectedOption))}`, textX, yPos + 26);

            yPos += imgHeight + 8;
            imageAdded = true;
          }
        } catch (err) {
          console.error('Error capturing image:', err);
        }
      }

      // Fallback to text only if image wasn't added
      if (!imageAdded) {
        addText(`Aircraft: ${selectedOption.aircraft?.tail_number || 'N/A'}`, 10);
        addText(`Model: ${selectedOption.aircraft?.model || selectedOption.aircraft?.make_model || 'N/A'}`, 10);
        addText(`Price: ${formatPrice(getTotalCost(selectedOption))}`, 10);
      }

      yPos += 5;
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
    }

    // Contract sections
    const sections = [
      { title: 'General', content: 'This quote is based on aircraft and crew availability and is not confirmed until signed and payment is received. All prices quoted are accurate at the time of quotation. Upon acceptance of these terms and conditions, this document becomes a legally binding contract between AvSKY Charter Ventures Inc. and the undersigned. Quotes are subject to change/cancellation until these terms and conditions have been accepted, made payment AND received a booking confirmation from AvSKY Charter Ventures Inc. If payment is made and a quote changes prior to the flight, customer may elect to pay any price differential or cancel and be entitled to a full refund.' },
      { title: "FBO's/Handlers", content: 'AvSKY Charter Ventures Inc. will select fixed base operators/handlers at each airport on this itinerary. If customer requests a particular FBO, a surcharge may be applicable in the equivalent of additional ramp, handling, and parking changes and fuel cost per gallon differential to the preferred FBO selected by AvSKY Charter Ventures Inc.' },
      { title: 'Disclosures', content: 'Desired Transportation of any animals, medical oxygen, firearms or other hazardous materials or any other special requirements must be communicated beforehand to AvSKY Charter Ventures Inc. Smoking is strictly prohibited unless the brokered aircraft and/or operator specifically allows for it, and is arranged before booking of flight.' },
      { title: 'Partial Completion of Flights', content: 'AvSKY Charter Ventures Inc. is not liable for expenses incurred for replacement transportation in the event of a mechanical. In the case of a mechanical situation, AvSKY Charter Ventures Inc. may, at its discretion, provide substitute transportation which may be charged as an additional fee to the customer. In such cases, AvSKY Charter Ventures Inc. original charges will apply only on portions of the flight which have been completed. If a flight does not reach its destination due to weather, charges apply to any destination reached and return flight of the aircraft and crew (with or without passengers) to home base.' },
      { title: 'Responsibility', content: 'AvSKY Charter Ventures Inc. shall not be liable for any injury, damage, loss, expense, indirect, special or consequential damages, or other irregularity caused the defect of any vehicle or conveyance, or the negligence of any company or person engaged in conveying the passenger or carrying out the arrangements for your trip or by accident, delay, flight schedule, change, cancellation, sickness, weather, strikes, acts of war, quarantine, force majeure, or any similar cause. Our liability shall in any case be limited to the amount paid to us, and any claim shall be adjudicated in and governed by the laws of the state of FL. Any claim brought against AvSKY Charter Ventures Inc. shall be brought only to the state courts of Charlotte Country or nearest federal court, if such action must be brought in federal court. AvSKY Charter Ventures Inc. does not operate any aircraft and acts solely as a broker to facilitate the undersigned\'s travel.' },
      { title: 'Documentation', content: 'A government issued photo ID is required prior to boarding. Travel documents (passports, visas, etc.) are the responsibility of each passenger. Boarding will be denied to any passenger without proper documentation when such is required by law or US Customs. All passengers are required to carry a passport when traveling to and from the US. All documents required for international travel must be presented to the flight crew for review prior to boarding.' },
      { title: 'Additional Terms', content: 'The undersigned agrees to indemnify and hold harmless AvSKY Charter Ventures Inc. from any and all liability associated with harm or injury that may be caused by the Aircraft operator. The customer agrees to provide AvSKY Charter Ventures Inc. any additional information requested including but not limited to passenger information and luggage weights.' },
      { title: 'Charges and Payment', content: 'Please note that the total cost of trip may be affected due to additional charges such as hangaring, de-icing, ATC delays, flight phone, ground transportation, weather delays, catering, and waiting time. Any damage or excessive cleaning charges to the aircraft incurred from passengers/customer will be charged accordingly. Invoice is due in full upon receipt. If payment becomes past due, customer will be responsible for all financing charges and/or collection fees including reasonable attorney\'s fees and court costs.' },
      { title: 'CANCELLATION POLICY', content: 'The following cancellation fees will apply should this trip be cancelled or the itinerary altered by the client in such a manner that would prohibit the customer from performing the trip (including any incurred expenses):\n\nAll trips are nonrefundable upon booking unless stated otherwise by AvSky Charter Ventures Inc. at their sole discretion, at which point there will automatically be a $1,000 cancellation fee applied.\n\nIn addition to cancellation charges, all cancellations, regardless of when they occur, are subject to payment of any actual expenses incurred by Charter Operator.' }
    ];

    sections.forEach(section => {
      addText(section.title + ':', 11, true);
      addText(section.content, 10);
      yPos += 3;
    });

    // Digital Signature Section
    yPos += 10;

    // Check if we need a new page for signature section
    if (yPos > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      yPos = 20;
    }

    doc.setDrawColor(200);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Digital Signature', margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const agreementText = 'I have read and understand the terms and conditions stated herein; upon acceptance this document becomes a legal and binding contract and I hereby accept these terms and conditions. Any modification to this contract must be agreed to by AvSKY Charter Ventures Inc. in writing. Terms and conditions stated herein are accepted upon purchase of the trip.';
    const agreementLines = doc.splitTextToSize(agreementText, maxWidth);
    doc.text(agreementLines, margin, yPos);
    yPos += agreementLines.length * 5 + 10;

    // Signature details
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Full Name:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(fullName || '____________________________', margin + 30, yPos);
    yPos += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    doc.text(currentDate, margin + 30, yPos);
    yPos += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Agreement:', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(agreeToTerms ? 'Accepted' : 'Pending', margin + 30, yPos);
    yPos += 15;

    // Signature line
    doc.setDrawColor(100);
    doc.line(margin, yPos, margin + 80, yPos);
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Signature', margin, yPos);

    // Save the PDF
    const fileName = `Charter_Contract_${quote?.quote_number || quoteId || 'document'}.pdf`;
    doc.save(fileName);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quote details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <RiErrorWarningLine className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Quote</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadQuoteDetails}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <RiRefreshLine className="h-4 w-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No quote data
  if (!quote) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4">
            <RiErrorWarningLine className="h-8 w-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Quote Not Found</h2>
          <p className="text-gray-600">The quote you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen overflow-x-hidden pb-5"
      style={{ background: 'linear-gradient(180deg, #477BB4 0%, #477BB4 40%, rgba(255, 255, 255, 0.5) 75%, rgba(255, 255, 255, 1) 100%)' }}
    >
      <div className="min-h-screen max-w-7xl mx-auto flex flex-col lg:flex-row items-start gap-4 lg:gap-8 p-4 lg:p-12">
        {/* Left Panel - Logo and Trip Title (Fixed) */}
        <div className="w-full lg:w-[25%] pt-4 lg:pt-8 flex flex-col justify-start flex-shrink-0">

          {/* Company Logo */}
          {organization?.branding?.logo ? (
          <img
            src={`${process.env.REACT_APP_API_URL}{organization?.branding?.logo}`}
            alt={organization?.name || "TrustJet"}
            className="h-16 lg:h-20 w-auto object-contain object-left mb-4 lg:mb-8"
          />
          ) : (
          <span className="text-2xl font-bold text-white mb-4 lg:mb-8">{organization?.name || "TrustJet"}</span>
          )}

          {/* Trip Title */}
          <p className="text-lg font-bold text-white mb-2">
            Trip: {(() => {
                const legs = quote?.bid_items?.flatMap(item => item.bid?.leg_itinerary || []) || [];
                if (legs.length > 1) {
                  return legs.map(l => `${l.departure_airport} → ${l.arrival_airport}`).join(' | ');
                }
                return `${quote?.departure_airport} → ${quote?.arrival_airport}`;
              })()}
          </p>
          <p className="text-white/80 text-base lg:text-lg">
            Quote for {quote.client?.first_name || ''} {quote.client?.last_name || ''}
          </p>
        </div>

        {/* Right Panel - Rounded Card with internal scroll */}
        <div className="w-full lg:w-[80%] relative overflow-hidden rounded-3xl">
          <style>
            {`
              .slide-container {
                display: flex;
                width: 200%;
                transition: transform 0.4s ease-in-out;
              }
              .slide-container.show-contract {
                transform: translateX(-50%);
              }
              .slide-panel {
                width: 50%;
                flex-shrink: 0;
              }
            `}
          </style>
          <div className={`slide-container ${showContract ? 'show-contract' : ''}`}>
            {/* Panel 1: Trip Selection */}
            <div className="slide-panel">
              <div
                className="rounded-3xl p-4 lg:p-8 max-h-[70vh] lg:max-h-[calc(100vh-6rem)] overflow-y-auto"
                style={{
                  background: 'linear-gradient(180deg, #E8EDF5 0%, #E8EDF5 70%, rgba(255, 255, 255, 1) 100%)'
                }}
              >
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Select your trip</h2>

        {/* Trip Details Card */}
        <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-4">TRIP DETAILS</p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Passenger Count */}
            <div className="flex items-start gap-2.5">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                  <RiTeamLine className="h-4 w-4 text-gray-600"/>
                </div>
                <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">Passenger Count</p>
                <p className="text-lg font-semibold text-gray-900">{quote.passenger_count || 0}</p>
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                <RiMapPinLine className="h-4 w-4 text-gray-600"/>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">{quote.is_round_trip ? 'Round Trip' : quote.is_multi_leg ? 'Multi-Leg' : 'One-Way'}</p>
                <p className="text-lg font-semibold text-gray-900">
                  {(quote?.bid_items?.flatMap(item => item.bid?.leg_itinerary || []) || []).map((leg, i) => (
                        <span key={i}>
                          {leg.departure_airport} → {leg.arrival_airport}
                          {i < (quote?.bid_items?.flatMap(item => item.bid?.leg_itinerary || []) || []).length - 1 && <br />}
                        </span>
                      ))}
                </p>
              </div>
            </div>
          </div>

          {/* Flight Legs */}
          <div className="flex items-center gap-2.5 mt-4 mb-3">
            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
              <RiCalendar2Line className="h-4 w-4 text-gray-600"/>
            </div>
            <p className="text-xs text-gray-600">Departure Dates</p>
          </div>
          {(() => {
            const legs = quote.bid_items?.flatMap(item => item.bid?.leg_itinerary || []) || [];
              return legs?.map((leg, idx) => (
                <div key={idx} className="flex items-center py-3 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <RiFlightTakeoffLine className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">{leg.departure_airport}</span>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <RiFlightLandLine className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">{leg.arrival_airport}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 ml-4">
                    <div className="flex items-center gap-1">
                      <RiCalendar2Line className="h-4 w-4" />
                      <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "MMM D")}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <RiTimeLine className="h-4 w-4" />
                      <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "HH:mm")}</span>
                    </div>
                  </div>
                </div>
              ));
          })()}

          {/* Expiry Notice */}
          {quote.expiry_date && (
            <div className="bg-gray-50 rounded-xl px-4 py-2 mt-3 flex items-center justify-center gap-2">
              <RiCalendar2Line className="h-4 w-4 text-gray-400" />
              <span className="text-xs text-gray-500">Expires on {formatExpiryDate(quote.expiry_date)}</span>
            </div>
          )}
        </div>

        {/* Aircraft Options - Horizontal Scroll */}
        {quote.bid_items && quote.bid_items.length > 0 && (
          <div
            ref={aircraftScrollRef}
            className="flex flex-col lg:flex-row gap-3 lg:overflow-x-auto pb-4 lg:cursor-grab [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-gray-400 scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300"
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {quote.bid_items.flatMap((bidItem) =>
              bidItem.bid?.bid_options?.map((option, index) => (
                <div key={index} className="w-full lg:w-[calc(50%-6px)] lg:min-w-[280px] lg:flex-shrink-0 bg-white rounded-2xl overflow-hidden shadow-sm">
                  {/* Aircraft Image */}
                  <div className="relative">
                    <img
                      src={option.aircraft?.image_urls && option.aircraft?.image_urls.length > 0 ? `${process.env.REACT_APP_API_URL}${option.aircraft?.image_urls[0]}` : TrustJetPlane}
                      className="w-full h-[180px] object-cover"
                      alt={option.aircraft?.tail_number || 'Aircraft'}
                    />
                  </div>

                  {/* Aircraft Details */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">{option.aircraft?.tail_number}</h4>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <RiPlaneLine className="h-3.5 w-3.5"/>
                          {getAircraftModelName(option.aircraft)}
                        </p>
                        {option.operator?.name && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <RiBuildingLine className="h-3.5 w-3.5"/>
                            {getOperatorWithLocation(option.operator)}
                          </p>
                        )}
                      </div>
                      {/*(option.operator?.trust_score !== null && option.operator?.trust_score !== undefined) && (
                        <TrustScore score={option.operator.trust_score} size="sm" />
                      )*/}
                    </div>

                    {/* Price */}
                    <div className="inline-flex items-center px-2.5 py-1 rounded-xl gap-1.5 mb-3 bg-accent">
                      <RiPriceTag3Line className="h-3.5 w-3.5 text-[#0C3E71]" />
                      <span className="text-sm font-semibold text-[#0C3E71]">{formatPrice(getTotalCost(option))}</span>
                    </div>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {option.operator?.argus_rating && option.operator?.argus_rating !== "No" && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
                          <RiTrophyLine className="h-3 w-3" /> {option.operator?.argus_rating}
                        </span>
                      )}
                      {option.operator?.wyvern_rating && option.operator?.wyvern_rating !== "No" && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
                          <RiMedalLine className="h-3 w-3" /> WINGMAN
                        </span>
                      )}
                      {option.aircraft?.aircraft_features?.no_aog && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                          <RiCheckLine className="h-3 w-3" /> NO AOG
                        </span>
                      )}
                    </div>

                    {/* Amenities */}
                    <div className="mb-3">
                      <h6 className="text-xs text-gray-500 font-medium mb-1.5">Amenities</h6>
                      {option.aircraft?.aircraft_features?.custom_features && option.aircraft?.aircraft_features?.custom_features?.length > 0 ? (
                        <div className="space-y-1">
                          {(expandedAmenities[option.id] ? option.aircraft.aircraft_features.custom_features : option.aircraft.aircraft_features.custom_features.slice(0, 3)).map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-center gap-1.5">
                              <RiCheckLine className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                              <span className="text-xs text-gray-500 truncate">{feature}</span>
                            </div>
                          ))}
                          {option.aircraft.aircraft_features.custom_features.length > 3 && (
                            <button
                              type="button"
                              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                              onClick={() => setExpandedAmenities(prev => ({ ...prev, [option.id]: !prev[option.id] }))}
                            >
                              {expandedAmenities[option.id] ? 'Show less' : `+${option.aircraft.aircraft_features.custom_features.length - 3} more`}
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">No amenities included.</p>
                      )}
                    </div>

                    {/* Book Button */}
                    <button
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-black text-white text-sm rounded-full transition-colors"
                      onClick={() => handleSelectFlight(option)}
                    >
                      <RiPlaneLine className="w-4 h-4" />
                      <span>Select This Flight</span>
                    </button>
                  </div>
                </div>
              )) || []
            )}
          </div>
        )}
              </div>
            </div>

            {/* Panel 2: Contract Signing */}
            <div className="slide-panel">
              <div
                className="rounded-3xl p-4 lg:p-8 max-h-[70vh] lg:max-h-[calc(100vh-6rem)] overflow-y-auto"
                style={{
                  background: 'linear-gradient(180deg, #E8EDF5 0%, #E8EDF5 70%, rgba(255, 255, 255, 1) 100%)'
                }}
              >
                {/* Contract Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl">
                      <RiFileTextLine className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900">Contract Signing</h2>
                      <p className="text-sm text-gray-500">Read and agree to the chartering contract</p>
                    </div>
                  </div>
                  <button
                    onClick={handleDownloadContract}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                    title="Download Contract PDF"
                  >
                    <RiDownloadLine className="w-4 h-4" />
                    <span className="hidden sm:inline">Download PDF</span>
                  </button>
                </div>

                {/* Selected Flight Summary */}
                {selectedOption && (
                  <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">SELECTED FLIGHT</p>
                    <div className="flex items-center gap-4">
                      <img
                        ref={selectedAircraftImageRef}
                        src={selectedOption.aircraft?.image_urls && selectedOption.aircraft?.image_urls.length > 0 ? `${process.env.REACT_APP_API_URL}${selectedOption.aircraft?.image_urls[0]}` : TrustJetPlane}
                        className="w-20 h-14 object-cover rounded-lg"
                        alt={selectedOption.aircraft?.tail_number || 'Aircraft'}
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{selectedOption.aircraft?.tail_number}</h4>
                        <p className="text-sm text-gray-500">{selectedOption.aircraft?.model || selectedOption.aircraft?.make_model}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-primary-600">{formatPrice(getTotalCost(selectedOption))}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contract Content */}
                <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-2">AvSKY Charter Ventures Inc.</h3>
                  <p className="text-xs text-gray-500 mb-4">Charter Terms and Conditions</p>
                  <div className="max-h-[250px] overflow-y-auto text-sm text-gray-600 space-y-4 pr-2">
                    <p className="text-xs text-gray-500">
                      1460 S. McCall Rd. Bldg-A, Unit 3G, Englewood, FL, 34223 | Phone: 720-660-7703 | cameron@iflyavsky.com
                    </p>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">General:</p>
                      <p>This quote is based on aircraft and crew availability and is not confirmed until signed and payment is received. All prices quoted are accurate at the time of quotation. Upon acceptance of these terms and conditions, this document becomes a legally binding contract between AvSKY Charter Ventures Inc. and the undersigned. Quotes are subject to change/cancellation until these terms and conditions have been accepted, made payment AND received a booking confirmation from AvSKY Charter Ventures Inc. If payment is made and a quote changes prior to the flight, customer may elect to pay any price differential or cancel and be entitled to a full refund.</p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">FBO's/Handlers:</p>
                      <p>AvSKY Charter Ventures Inc. will select fixed base operators/handlers at each airport on this itinerary. If customer requests a particular FBO, a surcharge may be applicable in the equivalent of additional ramp, handling, and parking changes and fuel cost per gallon differential to the preferred FBO selected by AvSKY Charter Ventures Inc.</p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">Disclosures:</p>
                      <p>Desired Transportation of any animals, medical oxygen, firearms or other hazardous materials or any other special requirements must be communicated beforehand to AvSKY Charter Ventures Inc. Smoking is strictly prohibited unless the brokered aircraft and/or operator specifically allows for it, and is arranged before booking of flight.</p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">Partial Completion of Flights:</p>
                      <p>AvSKY Charter Ventures Inc. is not liable for expenses incurred for replacement transportation in the event of a mechanical. In the case of a mechanical situation, AvSKY Charter Ventures Inc. may, at its discretion, provide substitute transportation which may be charged as an additional fee to the customer. In such cases, AvSKY Charter Ventures Inc. original charges will apply only on portions of the flight which have been completed. If a flight does not reach its destination due to weather, charges apply to any destination reached and return flight of the aircraft and crew (with or without passengers) to home base.</p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">Responsibility:</p>
                      <p>AvSKY Charter Ventures Inc. shall not be liable for any injury, damage, loss, expense, indirect, special or consequential damages, or other irregularity caused the defect of any vehicle or conveyance, or the negligence of any company or person engaged in conveying the passenger or carrying out the arrangements for your trip or by accident, delay, flight schedule, change, cancellation, sickness, weather, strikes, acts of war, quarantine, force majeure, or any similar cause. Our liability shall in any case be limited to the amount paid to us, and any claim shall be adjudicated in and governed by the laws of the state of FL. Any claim brought against AvSKY Charter Ventures Inc. shall be brought only to the state courts of Charlotte Country or nearest federal court, if such action must be brought in federal court. AvSKY Charter Ventures Inc. does not operate any aircraft and acts solely as a broker to facilitate the undersigned's travel.</p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">Documentation:</p>
                      <p>A government issued photo ID is required prior to boarding. Travel documents (passports, visas, etc.) are the responsibility of each passenger. Boarding will be denied to any passenger without proper documentation when such is required by law or US Customs. All passengers are required to carry a passport when traveling to and from the US. All documents required for international travel must be presented to the flight crew for review prior to boarding.</p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">Additional Terms:</p>
                      <p>The undersigned agrees to indemnify and hold harmless AvSKY Charter Ventures Inc. from any and all liability associated with harm or injury that may be caused by the Aircraft operator. The customer agrees to provide AvSKY Charter Ventures Inc. any additional information requested including but not limited to passenger information and luggage weights.</p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">Charges and Payment:</p>
                      <p>Please note that the total cost of trip may be affected due to additional charges such as hangaring, de-icing, ATC delays, flight phone, ground transportation, weather delays, catering, and waiting time. Any damage or excessive cleaning charges to the aircraft incurred from passengers/customer will be charged accordingly. Invoice is due in full upon receipt. If payment becomes past due, customer will be responsible for all financing charges and/or collection fees including reasonable attorney's fees and court costs.</p>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800 mb-1">CANCELLATION POLICY:</p>
                      <p>The following cancellation fees will apply should this trip be cancelled or the itinerary altered by the client in such a manner that would prohibit the customer from performing the trip (including any incurred expenses):</p>
                      <p className="mt-2 font-medium">All trips are nonrefundable upon booking unless stated otherwise by AvSky Charter Ventures Inc. at their sole discretion, at which point there will automatically be a $1,000 cancellation fee applied.</p>
                      <p className="mt-2">In addition to cancellation charges, all cancellations, regardless of when they occur, are subject to payment of any actual expenses incurred by Charter Operator.</p>
                    </div>
                  </div>
                </div>

                {/* Signature Section */}
                <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Digital Signature</h3>

                  {/* Full Name Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <RiUserLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter your full legal name"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Agreement Checkbox */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-600">
                     I have read and understand the terms and conditions stated herein; upon acceptance this document becomes a legal and binding contract and I hereby accept these terms and conditions. Any modification to this contract must be agreed to by AvSKY Charter Ventures Inc. in writing. Terms and conditions stated herein are accepted upon purchase of the trip.
                    </span>
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {!optionId && (
                  <button
                    onClick={handleBackToTrips}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-50 transition-colors"
                  >
                    <RiArrowLeftLine className="w-4 h-4" />
                    Back
                  </button>
                  )}
                  <button
                    onClick={handleProceedToCheckout}
                    disabled={!agreeToTerms || !fullName.trim() || isSubmitting}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-full transition-colors ${
                      agreeToTerms && fullName.trim() && !isSubmitting
                        ? 'bg-black text-white hover:bg-gray-800'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <span>Next</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotePreviewPage;